import { hashEmail } from '../utils/cryptoUtils'
import { getQuizUserId, sendGtmEvent } from './gtmUtils'
import type { LinkPosition } from './typesV2'

// ── Complex-trial helpers (mirrors membershipGtm's trial handling) ──

const getTrialLengthFromItems = (items: Daily30PlanItem[]): number | undefined => {
  for (const item of items) {
    if (item.trialLengthDays) return item.trialLengthDays
  }
  return undefined
}

const mapDaily30Items = (items: Daily30PlanItem[]) =>
  items.map((item) => {
    const trialLength = item.trialLengthDays
    return {
      ...item,
      item_brand: 'ZOE',
      item_category: 'Daily30',
      item_category2: 'Gut supplement',
      ...(trialLength && { item_category5: String(trialLength) }),
    }
  })

type TrackPDPViewEventProps = {
  currency: string
  user_id?: string
  user_email?: string
}

export const trackDaily30PDPViewEvent = ({ currency, user_id, user_email }: TrackPDPViewEventProps) => {
  const event = {
    event: 'view_item',
    section: 'pdp',
    page_name: 'pdp',
    product_type: 'daily30',
    product_selected: 'daily30',
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: [
        {
          item_id: 'daily30_pdp',
          item_name: 'Daily30 (PDP)',
          item_brand: 'ZOE',
          item_category: 'Daily30',
        },
      ],
    },
    ...((user_id || user_email) && { user_id: user_id || hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

export type Daily30PlanItem = {
  item_bundle: string
  item_id: string
  item_name: string
  item_category3: string
  item_category4: string
  price: number
  discount?: number
  quantity: number
  /** Trial length normalised to days. Present only for complex trial items. */
  trialLengthDays?: number
}

type TrackDaily30PlanSelectEventProps = {
  currency: string
  link_position?: LinkPosition
  /** Lookup key of the plan being switched away from. Absent on a first selection. */
  previous_product_selected?: string
  items: Daily30PlanItem[]
  user_email?: string
}

export const trackDaily30PlanSelectEvent = ({
  currency,
  link_position,
  previous_product_selected,
  items,
  user_email,
}: TrackDaily30PlanSelectEventProps) => {
  const trialLength = getTrialLengthFromItems(items)
  const event = {
    event: 'select_item',
    section: 'plans',
    page_name: 'product_selected',
    product_type: 'daily30',
    product_selected: items[0]?.item_bundle || items[0]?.item_id,
    ...(link_position && { link_position }),
    ...(previous_product_selected && { previous_product_selected }),
    ...(trialLength && { order_trial_length: trialLength }),
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapDaily30Items(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

// Fires when a user navigates a PDP content carousel (reviews, scientists, RCT
// results) — mirrors the bars hero-carousel `user_click` tracking so analytics can
// see how far into a row a user gets. The 1-based position is appended to the label.
export const trackDaily30CarouselClick = (text: string, index: number) => {
  const userId = getQuizUserId()
  const label = text.trim() || 'card'
  sendGtmEvent({
    event: 'user_click',
    click_type: 'carousel',
    section: 'pdp',
    page_name: 'pdp',
    product_type: 'daily30',
    link_position: 'page_body',
    link_text_image: `${label} (${index + 1})`,
    ...(userId && { user_id: userId }),
  })
}

type TrackBeginDaily30CheckoutEventProps = {
  user_type: 'New' | 'Existing' | 'Guest'
  currency: string
  items: Daily30PlanItem[]
  user_id?: string
  user_email?: string
}

export const trackBeginDaily30CheckoutEvent = ({
  user_type,
  currency,
  items,
  user_email,
}: TrackBeginDaily30CheckoutEventProps) => {
  const trialLength = getTrialLengthFromItems(items)
  const event = {
    event: 'begin_checkout',
    user_type: user_type,
    section: 'checkout',
    page_name: 'checkout',
    product_type: 'daily30',
    product_selected: items[0]?.item_bundle || items[0]?.item_id,
    ...(trialLength && { order_trial_length: trialLength }),
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapDaily30Items(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type UserData = {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  country: string
  city: string
  postal_code: string
}

type TrackDaily30PurchaseEventProps = {
  user_type: 'New' | 'Existing' | 'Guest'
  currency: string
  tax?: number
  shipping?: number
  coupon?: string
  transaction_id: string
  value: number
  shipping_tier?: string
  payment_type: string
  items: Daily30PlanItem[]
  user_data: UserData
  user_email?: string
  additionalItems?: Array<Record<string, unknown>>
  productSelected?: string
}

export const trackDaily30PurchaseEvent = ({
  user_email,
  user_type,
  currency,
  tax,
  shipping,
  coupon,
  transaction_id,
  value,
  shipping_tier,
  payment_type,
  items,
  user_data,
  additionalItems = [],
  productSelected,
}: TrackDaily30PurchaseEventProps) => {
  const trialLength = getTrialLengthFromItems(items)
  const event = {
    event: 'purchase',
    user_type: user_type,
    section: 'checkout',
    page_name: 'purchase',
    product_type: 'daily30',
    product_selected: productSelected ?? (items[0]?.item_bundle || items[0]?.item_id),
    ...(trialLength && { order_trial_length: trialLength }),
    ecommerce: {
      currency: currency?.toUpperCase(),
      tax,
      coupon,
      transaction_id,
      value,
      payment_type,
      items: [...mapDaily30Items(items), ...additionalItems],
      ...(shipping && { shipping }),
      ...(shipping_tier && { shipping_tier }),
    },
    user_data,
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}
